import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRequest } from '@/hooks';
import { coreApi } from '@/api';
import { PageViewEventName, track } from '@/track';
import SelectChannelPurpose from './components/SelectChannelPurpose';
import CreateChannelForm from './components/CreateChannelForm';

type Step = 'purpose' | 'form' | 'completed';

/**
 * `return` 쿼리 값이 안전한 상대 경로인지 확인한다.
 * 외부 도메인 redirect를 방지하기 위해 `/` 로 시작하는 단일 경로만 허용한다.
 *
 *   /connect/resume        ✓ 허용
 *   //evil.com             ✗ 거부 (protocol-relative URL)
 *   http://evil.com        ✗ 거부 (절대 URL)
 *   /path?x=1&y=2          ✓ 허용
 */
function isSafeReturnPath(value: string | null): value is string {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  // protocol-relative URL 차단 (//evil.com)
  if (value.startsWith('//')) return false;
  // 백슬래시 우회 차단 (/\evil.com)
  if (value.startsWith('/\\')) return false;
  return true;
}

export default function CreateAppPage() {
  const [currentStep, setCurrentStep] = useState<Step>('form');
  const [channelData, setChannelData] = useState<{ name: string; description: string } | null>(
    null
  );
  const [createdChannel, setCreatedChannel] = useState<ApplicationResponseModel | null>(null);
  const [selectedPurpose, setSelectedPurpose] = useState<UsePurposeType | null>(null);
  const history = useHistory();
  const location = useLocation();

  // `?return=` 쿼리 — 외부에서 채널 생성 후 특정 경로로 돌려보내고 싶을 때 사용한다.
  // 현재 사용처: /connect/resume (학교 연동 흐름에서 채널 생성 후 복귀)
  const returnPath = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('return');
    return isSafeReturnPath(raw) ? raw : null;
  }, [location.search]);

  useEffect(() => {
    track.onPageView({ pageViewEventName: PageViewEventName.view_newchannelpage });
  }, []);

  const { mutate } = useRequest<CommonResponseModel<ApplicationResponseModel>[]>(
    '/apps',
    undefined,
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );

  const handleFormNext = (data: { name: string; description: string }) => {
    setChannelData(data);
    setCurrentStep('purpose');
  };

  const handlePurposeSubmit = async (purpose: UsePurposeType, otherText?: string) => {
    if (!channelData) {
      return;
    }

    const createdChannelData: CreateAppRequestModel = {
      ...channelData,
      contactPhone: null,
      contactEmail: null,
      channelType: 'Normal',
      usePurposeType: purpose,
      usePurposeNote: otherText ?? null
    };

    const { data: response } = await coreApi.post<CommonResponseModel<ApplicationResponseModel>>(
      '/apps',
      createdChannelData,
      {
        headers: {
          'x-rwd-api-version': '1.1'
        }
      }
    );

    await mutate();

    setSelectedPurpose(purpose);
    setCreatedChannel(response.data);
    setCurrentStep('completed');
  };

  useEffect(() => {
    if (currentStep === 'completed' && createdChannel && selectedPurpose) {
      // `?return=` 쿼리가 있으면 해당 경로로 redirect. 채널 ID 정보는 sessionStorage 기반 흐름에서
      // 별도로 복구되므로 여기서는 단순 location.assign 만 수행한다.
      if (returnPath) {
        window.location.assign(returnPath);
        return;
      }

      history.push(`/apps/${createdChannel.id}`, {
        purpose: selectedPurpose,
        onboardingCompleted: true
      });
    }
  }, [currentStep, createdChannel, selectedPurpose, history, returnPath]);

  return (
    <>
      {currentStep === 'form' && <CreateChannelForm onNext={handleFormNext} />}
      {currentStep === 'purpose' && <SelectChannelPurpose onSubmit={handlePurposeSubmit} />}
      {/* {currentStep === 'completed' && (
        <>
          <Confetti />
          {createdChannel && (
            <div css={containerForCreated}>
              <div css={headerContainer}>
                <PartyFaceIcon />
                <div css={headerStyle}>채널 생성 완료!</div>
              </div>
              <div css={contentStyle}>
                <span css={textBold}>{createdChannel.name}</span>
                에서 <br />
                크리에이터님만의 멋진 이야기를 실현해보세요.
              </div>
              <button
                css={buttonStyle}
                type="button"
                onClick={() => history.push(`/apps/${createdChannel.id}`)}
              >
                채널로 이동하기
              </button>
            </div>
          )}
        </>
      )} */}
    </>
  );
}

// const containerForCreated = css`
//   padding: 24px 24px 0;
//   background-color: #ffffff;
//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   justify-content: center;
//   width: 100%;
//   height: 100%;
//   gap: 12px;
// `;

// const headerContainer = css`
//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   gap: 16px;
//   max-width: 320px;
// `;

// const headerStyle = css`
//   color: #0e1017;
//   text-align: center;
//   font-size: 20px;
//   font-style: normal;
//   font-weight: 700;
//   line-height: 24px;
//   display: flex;
// `;

// const contentStyle = css`
//   text-align: center;
//   color: #0e1017;
//   font-family: Pretendard, sans-serif;
//   font-size: 16px;
//   font-style: normal;
//   font-weight: 500;
//   line-height: 150%;
//   margin-bottom: 28px;
// `;

// const textBold = css`
//   color: #7800ff;
//   font-weight: 700;
// `;

// const buttonStyle = css`
//   max-width: 320px;
//   width: 100%;
//   border: none;
//   display: flex;
//   justify-content: center;
//   padding: 12px;
//   border-radius: 8px;
//   font-family: Pretendard, sans-serif;
//   font-size: 16px;
//   font-style: normal;
//   font-weight: 500;
//   line-height: 19px;
//   background: #7800ff;
//   color: #ffffff;
//   cursor: pointer;
// `;
